import { useState } from 'react'
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import type { PropertyCalendarDeposit, PropertyCalendarDepositEvent } from '@/api/client'
import {
  buildTimelineRows,
  depositDotClasses,
  depositEventLabel,
  formatDepositInstant,
  timeZoneName,
} from '@/lib/deposit-events'
import { cn } from '@/lib/utils'
import { SectionLoading } from './section-loading'

/**
 * What the calendar read says about this stay's deposit. `pending` is "not
 * read yet", shown as a loading block; an `error` layer is said out loud, because an
 * unreadable layer is not "no deposit"; `ok` with no deposit is a stay that has
 * none in the read, which also shows nothing (the deposit card above is the one
 * that speaks for the reservation itself).
 */
export type StayDeposit =
  | { status: 'pending' }
  | { status: 'error'; message: string }
  | { status: 'ok'; deposit: PropertyCalendarDeposit | null }

interface DepositTimelineProps {
  deposit: StayDeposit
  /** The property's zone, from the calendar read: every time below is on that clock. */
  timeZone: string
}

function Dot({ type, planned }: { type: string; planned: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'mt-1 block h-2.5 w-2.5 shrink-0 rounded-full border-2',
        depositDotClasses(type, planned)
      )}
    />
  )
}

function EventRow({ event, timeZone }: { event: PropertyCalendarDepositEvent; timeZone: string }) {
  return (
    <li
      className="flex items-start gap-2.5"
      data-testid="deposit-timeline-event"
      data-event-type={event.type}
      data-planned={event.planned ? 'true' : 'false'}
    >
      <Dot type={event.type} planned={event.planned} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-sm font-medium">{depositEventLabel(event.type)}</span>
          {event.planned && (
            <Badge variant="secondary" className="text-[10px]">
              Planned
            </Badge>
          )}
        </div>
        <time className="text-xs tabular-nums text-textMuted" dateTime={event.at}>
          {formatDepositInstant(event.at, timeZone)}
        </time>
      </div>
    </li>
  )
}

/**
 * A run of the same event, folded: "Hold renewed 42 times", with the first and
 * last instant and a switch that lists every one. A long stay's hold can be
 * renewed hourly for a day when it keeps lapsing; the row says so once instead
 * of burying the dates around it.
 */
function RunRow({
  type,
  planned,
  events,
  timeZone,
}: {
  type: string
  planned: boolean
  events: PropertyCalendarDepositEvent[]
  timeZone: string
}) {
  const [open, setOpen] = useState(false)
  const first = events[0]
  const last = events[events.length - 1]
  const listId = `deposit-run-${first.at}`
  return (
    <li
      className="flex items-start gap-2.5"
      data-testid="deposit-timeline-run"
      data-event-type={type}
      data-planned={planned ? 'true' : 'false'}
    >
      <Dot type={type} planned={planned} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-sm font-medium">
            {depositEventLabel(type)} {events.length} times
          </span>
          {planned && (
            <Badge variant="secondary" className="text-[10px]">
              Planned
            </Badge>
          )}
        </div>
        <p className="text-xs tabular-nums text-textMuted">
          <time dateTime={first.at}>{formatDepositInstant(first.at, timeZone)}</time>
          {' to '}
          <time dateTime={last.at}>{formatDepositInstant(last.at, timeZone)}</time>
        </p>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => setOpen(value => !value)}
          className="mt-1 inline-flex items-center gap-1 rounded-sm text-xs text-mint hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2"
        >
          {open ? (
            <ChevronDown className="h-3 w-3" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-3 w-3" aria-hidden="true" />
          )}
          {open ? 'Hide each one' : `Show all ${events.length}`}
        </button>
        {open && (
          <ol id={listId} className="mt-2 space-y-2 border-l border-border pl-3">
            {events.map((event, index) => (
              <EventRow key={`${event.at}:${index}`} event={event} timeZone={timeZone} />
            ))}
          </ol>
        )}
      </div>
    </li>
  )
}

/**
 * The stay's deposit events in the order the API returned them: recorded ones
 * (filled) and planned ones (outlined). Read-only - the hold is renewed and
 * released by the automation, and charging or refunding happens in Stripe, via
 * the link on the deposit card above.
 *
 * The order is the API's and is never changed here: an event that arrives out of
 * sequence is shown where it came, not tidied into place.
 */
export function DepositTimeline({ deposit, timeZone }: DepositTimelineProps) {
  if (deposit.status === 'pending') return <SectionLoading label="Deposit timeline" />

  if (deposit.status === 'error') {
    return (
      <section aria-label="Deposit timeline" className="space-y-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-textFaint">
          Deposit timeline
        </h3>
        <p className="flex items-center gap-1.5 text-sm text-danger" role="status">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {deposit.message}
        </p>
      </section>
    )
  }

  if (!deposit.deposit) return null
  const { events, held_by: heldBy } = deposit.deposit
  const rows = buildTimelineRows(events)

  return (
    <section aria-label="Deposit timeline" className="space-y-3" data-testid="deposit-timeline">
      <div className="space-y-1">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-textFaint">
          Deposit timeline
        </h3>
        <p className="text-xs text-textMuted">
          Times are {timeZoneName(timeZone)}. Filled dots happened, outlined ones are planned.
        </p>
        {heldBy === 'uplisting' && (
          <p className="text-xs text-textMuted">
            Uplisting holds this deposit, so this list only records what was seen; it renews and
            releases nothing.
          </p>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-textMuted">No deposit events yet.</p>
      ) : (
        <ol className="space-y-3">
          {rows.map(row =>
            row.kind === 'event' ? (
              <EventRow key={row.key} event={row.event} timeZone={timeZone} />
            ) : (
              <RunRow
                key={row.key}
                type={row.type}
                planned={row.planned}
                events={row.events}
                timeZone={timeZone}
              />
            )
          )}
        </ol>
      )}
    </section>
  )
}
